import { FilterInput, Flex, PrimaryButton } from '@wrisk/ui-components'
import { range } from 'lodash-es'
import { DateTime } from 'luxon'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { VehicleVIN } from '../../../../../domain'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { VehicleMakesFilter } from './VehicleMakesFilter'
import { VehicleModelsFilter } from './VehicleModelsFilter'
import { VehicleResultsFilter } from './VehicleResultsFilter'

export type VehicleSelectValue = Partial<{
  specification: VehicleVIN
  vrn: string
}>

interface VehicleSelectConfig {
  profile?: string
  earliestYear?: number
  vrnRequired?: boolean
}

export interface VehicleSelectVRNProps {
  config?: VehicleSelectConfig
  value?: VehicleSelectValue
  onChange: (specification: VehicleSelectValue | undefined) => void
}

const tKey = TKey('components.vehicle-select')

const isSpecification = (spec: Partial<VehicleVIN>): spec is VehicleVIN =>
  Boolean(spec.vehicleCode)

export const VehicleSelectVRN: FunctionComponent<VehicleSelectVRNProps> = ({
  config,
  value,
  onChange,
}) => {
  const { t } = useWriskTranslation()

  const [specification, setSpecification] = useState<Partial<VehicleVIN>>(
    value?.specification ?? {},
  )

  const years = useMemo(
    () => range(DateTime.local().get('year'), (config?.earliestYear ?? 1940) - 1),
    [config?.earliestYear],
  )

  const onInternalConfirm = useCallback(() => {
    if (isSpecification(specification)) {
      onChange({
        ...value,
        specification,
      })
    }
  }, [specification, onChange, value])

  const onResultSelect = useCallback((newSpecification?: VehicleVIN) => {
    setSpecification((prevState) => newSpecification ?? prevState)
  }, [])

  const setSpecificationAndResetSelection = useCallback(
    (newSpecification: Partial<VehicleVIN>) => {
      setSpecification(newSpecification)
      onChange(undefined)
    },
    [onChange],
  )

  const setYear = useCallback(
    (year: number | undefined) => {
      setSpecificationAndResetSelection({
        year,
      })
    },
    [setSpecificationAndResetSelection],
  )

  const setMake = useCallback(
    (make: string | undefined) => {
      setSpecificationAndResetSelection({
        year: specification.year,
        make,
      })
    },
    [specification, setSpecificationAndResetSelection],
  )

  const setModel = useCallback(
    (model: string | undefined) => {
      setSpecificationAndResetSelection({
        year: specification.year,
        make: specification.make,
        model,
      })
    },
    [specification, setSpecificationAndResetSelection],
  )

  const makesDropdown = specification.year && (
    <VehicleMakesFilter
      onSelect={setMake}
      profile={config?.profile}
      year={specification.year}
      make={specification.make}
    />
  )

  const modelsDropdown = specification.year && specification.make && (
    <VehicleModelsFilter
      onSelect={setModel}
      profile={config?.profile}
      year={specification.year}
      make={specification.make}
      model={specification.model}
    />
  )

  const vehiclesResults = specification.year &&
    specification.make &&
    specification.model && (
      <VehicleResultsFilter
        onSelect={onResultSelect}
        profile={config?.profile}
        year={specification.year}
        make={specification.make}
        model={specification.model}
        initialValue={specification as VehicleVIN}
      />
    )

  const disabled = !isSpecification(specification)

  return (
    <Flex
      justifyContent='flex-start'
      flexDirection='column'
      alignItems='flex-start'
      flexGrow={1}
      width={1}
      gap={3}
    >
      <FilterInput
        data-testid='vehicle-year'
        width={1}
        placeholder='Year of manufacture'
        value={specification.year}
        onSelect={setYear}
        options={years}
        renderOption={renderYear}
        renderOptionText={renderYear}
        matchOption={matchYear}
      />

      {makesDropdown}
      {modelsDropdown}
      {vehiclesResults}

      <PrimaryButton
        data-testid='vehicle-confirm'
        disabled={disabled}
        onClick={onInternalConfirm}
      >
        {t(tKey('actions.confirm'))}
      </PrimaryButton>
    </Flex>
  )
}

const renderYear = (year: number) => year.toString()
const matchYear = (text: string) => (yr: number) => yr.toString().includes(text)
